<template>
  <div
    class="EventDetailPage flex"
    :class="{ 'single-program': isSingleProgram }"
  >
    <div class="grid">
      <div class="col-1-1 no-p mb-xxlg">
        <div class="label-container col-1-1 no-p mb-md">
          <ul class="no-bullets flex">
            <li
              v-for="genre in event.genres"
              :key="genre.id"
              class="label mb-sm mr-8"
            >
              {{ genre.name }}
            </li>
          </ul>
        </div>
        <UIHeadingType
          heading-type="h1"
          :title="event.title"
          is-html
          class="event-title order-1 mb-0"
        />
      </div>

      <div class="col-1-1 no-p sm-full-bleed sm-mb-56 mb-xxlg flex sm-flex-column flex-nowrap">
        <div class="col-8-12 sm-col-1-1">
          <div
            class="image-container relative"
            :class="[{ 'sm-mb-sm': event.programs.length > 1, 'sm-mb-xxlg': event.programs.length <= 1 }]"
          >
            <div
              v-if="eventType"
              class="labels"
            >
              <span class="label">{{ eventType }}</span>
            </div>
            <BaseImage
              class="image"
              :media-object="event.image1"
              :sizes="{ lg: 800 }"
            >
              <template #fallback>
                <img
                  src="/images/empty-white.jpg"
                  style="width: 100%; height: 100%; object-fit: cover"
                />
              </template>
            </BaseImage>
          </div>
          <div class="sm-show hide">
            <UIEventsProgramOverview
              :program-single="programSingle"
              :program-single-readable="programSingleReadable"
              :programs="event.programs"
              @buy-click="convertBeginCheckout(event, $event)"
            />
          </div>
          <div class="breadcrumbs mt-xxlg mb-xlg">
            <UIBreadCrumbs :items="breadcrumbs" />
          </div>
          <div class="program-description">
            <p class="heading-3 mb-lg">
              {{ event.oneLiner }}
            </p>
            <div
              class="intro-text mb-lg"
              v-html="event.intro"
            />
            <div
              class="description raw"
              v-html="event.description"
            />
          </div>
        </div>

        <div class="col-4-12 sm-col-1-1 sm-hide">
          <UIEventsProgramOverview
            :program-single="programSingle"
            :program-single-readable="programSingleReadable"
            :programs="event.programs"
            @buy-click="convertBeginCheckout(event, $event)"
          />
        </div>
      </div>
    </div>

    <div class="col-1-1 no-p content-container">
      <ContentBlockContainer
        :contentblocks="event.content"
        :page="event"
        :main-event-id="event.id"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { DetailPageEvent } from '@/types/contentblocks'

  const props = defineProps<{
    event: DetailPageEvent
  }>()

  if (props.event.metadata) {
    const metaData = useMetaData()
    const mappedMetaData = metaData.map(props.event.metadata)

    useSeoMeta(mappedMetaData)
  }

  // function isExternalLink(url: string) {
  //   return /^https?:\/\//.test(url) // Checks if the URL starts with 'http' or 'https'
  // }

  const eventType = props.event.attributes?.find((attribute) => attribute.type.id === 5)?.name

  const { formatTime, formatEuro, getProgramDateString } = useEventsHelpers()

  const isSingleProgram = computed(() => {
    return props.event.programs?.length === 1
  })

  const programSingle = computed(() => {
    if (!props.event.programs?.length) {
      return null
    }
    if (props.event.programs.length === 1) {
      return {
        date: formatDateEventDetail(props.event.programs[0].startAt),
        time: `${Array.from(
          new Set([formatTime(props.event.programs[0].startAt), formatTime(props.event.programs[0].endAt)]),
        ).join(' - ')} uur`,
        // price: formatEuro(props.event.programs[0]?.price),
        location: props.event.programs[0]?.fields.locationDetailed || props.event.programs[0].location,
        button: props.event.programs[0].button,
      }
    }

    const sortedUniquePrices = Array.from(
      new Set(props.event.programs.map((program: { price: number }) => program.price)),
    ).sort((a, b) => a - b)
    const showPrices =
      sortedUniquePrices?.length > 1
        ? [sortedUniquePrices[0], sortedUniquePrices[sortedUniquePrices.length - 1]]
        : [sortedUniquePrices[0]]

    const button = {
      text: 'Data & Aanmelden',
      link: '#event-program-list',
    }

    return {
      price: showPrices.map((price) => formatEuro(price)).join(' - '),
      date: getProgramDateString(props.event),
      location: props.event.programs[0]?.fields.locationDetailed || props.event.programs[0].location,
      button,
    }
  })

  const programSingleReadable = computed(() => {
    const list = []
    if (!programSingle.value) {
      return list
    }

    if (programSingle.value.price) {
      list.push({
        label: 'Prijs',
        value: programSingle.value.price,
      })
    }

    if (programSingle.value.date) {
      list.push({
        label: 'Datum',
        value: programSingle.value.date,
      })
    }

    if (programSingle.value.time) {
      list.push({
        label: 'Tijd',
        value: programSingle.value.time,
      })
    }

    if (programSingle.value.location) {
      list.push({
        label: 'Locatie',
        value: programSingle.value.location.name,
      })
    }

    return list
  })

  const breadcrumbs = computed(() => {
    return [
      {
        name: 'Home',
        url: '/',
      },
      {
        name: 'Programma',
        url: '/programma',
      },
      {
        name: props.event.title,
        url: '',
      },
    ]
  })

  const { forEvent } = useStructuredData()

  useHead({
    script: [
      {
        type: 'application/ld+json',
        children: forEvent(props.event),
      },
    ],
  })

  const { convertBeginCheckout, trackViewItem } = useTracking()

  onMounted(() => {
    trackViewItem({ items: [eventToGA4Item(props.event)] })
  })
</script>

<style lang="scss" scoped>
  .EventDetailPage {
    padding: 8rem 0;

    .event-title-container {
      width: 100%;
    }

    .image-container {
      display: block;
      width: 100%;
      overflow: hidden;
      border-radius: 16px;
      aspect-ratio: 97 / 60;

      :deep(.ImageComponent) {
        width: 100%;
        height: 100%;

        .image-wrapper {
          width: 100%;
          height: 100%;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
      }

      :deep(.image-credits) {
        display: none;
      }
    }
  }

  @include media(sm) {
    .EventDetailPage {
      padding: var(--contentblock-margin) 0;

      &.single-program {
        .image-container {
          height: auto;

          .image {
            position: relative;
          }
        }

        .program-overview-container {
          min-height: unset;
        }
      }

      .image-container {
        aspect-ratio: 390 / 264;

        .labels,
        .labels .label {
          border-radius: 0 0 0 8px;
        }
      }

      .event-title {
        word-break: word-break;
        hyphens: auto;
        hyphenate-limit-chars: 14 4 4;
      }
    }
  }
</style>
